import { useMemo } from "react";
import { fetchEntryConditionsHistory } from "../api/client";
import { usePoll } from "./usePoll";

export interface UseHistoryOptions {
  lookbackMs: number;
  step: string;
  intervalMs?: number;
}

/**
 * Rolling-window history fetcher. Re-computes [start, end] each poll so the
 * window slides forward with real time. Quantize end to the step to keep the
 * request signature stable within a step — same requests within the same step
 * should benefit from any server-side caching.
 */
export function useEntryConditionsHistory(
  symbol: string,
  { lookbackMs, step, intervalMs = 30_000 }: UseHistoryOptions
) {
  const quantizeMs = useMemo(() => parseStepMs(step), [step]);
  return usePoll(
    (signal) => {
      const now = Date.now();
      const q = quantizeMs > 0 ? now - (now % quantizeMs) : now;
      const end = new Date(q);
      const start = new Date(q - lookbackMs);
      return fetchEntryConditionsHistory(symbol, start, end, step, signal);
    },
    intervalMs,
    [symbol, lookbackMs, step]
  );
}

function parseStepMs(step: string): number {
  const m = /^\s*(\d+)\s*(s|m|h)\s*$/i.exec(step);
  if (!m) return 0;
  const n = parseInt(m[1], 10);
  const unit = m[2].toLowerCase();
  const mul = unit === "h" ? 3_600_000 : unit === "m" ? 60_000 : 1_000;
  return n * mul;
}
